import { useTranslations } from 'next-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBookmark,
  faBookmarkSlash,
  faSpinner,
  faPlus,
  faCheck,
  faArrowUpRightFromSquare
} from '@fortawesome/pro-solid-svg-icons'

import { Link, useRouter } from '@/i18n/navigation'
import { Button } from '@/app/components/teqplay-ui'

export type TSearchResult = {
  imo: string
  name: string
}

export type TItemDefaultClick = 'GO_TO_VESSEL_PAGE' | 'ADD_TO_LINEUP' | 'FOLLOW_VESSEL'

type TSearchResultItemProps = {
  result: TSearchResult
  isBookmarked: boolean
  isLoading: boolean
  onLinkClick: () => void
  onFollowClick: (e: React.MouseEvent, result: TSearchResult) => void
  // Lineup mode props
  lineupMode?: boolean
  isInLineup?: boolean
  onAddToLineupClick?: (e: React.MouseEvent, result: TSearchResult) => void
  // Default click behavior
  itemDefaultClick?: TItemDefaultClick
}

export default function SearchResultItem({
  result,
  isBookmarked,
  isLoading,
  onLinkClick,
  onFollowClick,
  lineupMode = false,
  isInLineup = false,
  onAddToLineupClick,
  itemDefaultClick = 'GO_TO_VESSEL_PAGE'
}: TSearchResultItemProps) {
  const t = useTranslations('SearchResultsSection')
  const router = useRouter()

  const handleActionClick = (e: React.MouseEvent) => {
    if (lineupMode && onAddToLineupClick) {
      onAddToLineupClick(e, result)
    } else {
      onFollowClick(e, result)
    }
  }

  // Handle the main link/item click based on itemDefaultClick
  const handleItemClick = (e: React.MouseEvent) => {
    if (itemDefaultClick === 'GO_TO_VESSEL_PAGE') {
      // Default behavior - let the Link navigate
      onLinkClick()
      return
    }

    // Prevent navigation for non-default behaviors
    e.preventDefault()
    onLinkClick()

    if (itemDefaultClick === 'ADD_TO_LINEUP' && onAddToLineupClick) {
      onAddToLineupClick(e, result)
    } else if (itemDefaultClick === 'FOLLOW_VESSEL') {
      onFollowClick(e, result)
    }
  }

  // Navigate to vessel page (used by the link-out button)
  const handleGoToVesselPage = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onLinkClick()
    router.push(`/vessels/${result.imo}`)
  }

  const renderActionButton = () => {
    if (lineupMode) {
      return (
        <Button
          onClick={handleActionClick}
          aria-label={isInLineup ? t('inLineup') : t('addToLineup')}
          variant="link"
          disabled={isInLineup || isLoading}
        >
          <FontAwesomeIcon
            icon={isLoading ? faSpinner : isInLineup ? faCheck : faPlus}
            spin={isLoading}
          />
          {isInLineup ? t('inLineup') : t('addToLineup')}
        </Button>
      )
    }

    return (
      <Button
        onClick={handleActionClick}
        aria-label={t('follow')}
        variant="link"
        disabled={isBookmarked || isLoading}
      >
        <FontAwesomeIcon
          icon={isLoading ? faSpinner : isBookmarked ? faBookmarkSlash : faBookmark}
          spin={isLoading}
        />
        {isBookmarked ? t('following') : t('follow')}
      </Button>
    )
  }

  return (
    <div className="result-vessel">
      <Link href={`/vessels/${result.imo}`} className="vessel-link" onClick={handleItemClick}>
        <span className="vessel-info">
          {result.name} ({result.imo})
        </span>
        {itemDefaultClick !== 'GO_TO_VESSEL_PAGE' && (
          <Button
            variant="link"
            className="vessel-link-out-btn"
            onClick={handleGoToVesselPage}
            aria-label={t('goToVesselPage')}
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </Button>
        )}
      </Link>
      {renderActionButton()}
    </div>
  )
}
